import {useMutation, type FetchResult} from '@apollo/client';
import {useCallback} from 'react';
import {produce} from 'immer';

import {useTransformQuery} from 'web-app/react/hooks/use-transform-query';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {updateApolloCache} from 'web-app/util/graphql';
import {useCanAccessUSPlans} from 'web-app/react/profile/child-profile/plans-and-invoices/use-can-access-us-plans';

import {GetPlanTemplates, SavePlanTemplate} from '../queries/template.query';
import type {
    GetPlanTemplatesQuery,
    GetPlanTemplatesQueryVariables,
    SavePlanTemplateMutation,
    SavePlanTemplateMutationVariables,
} from '../queries/__generated__/template.query.api-types';
import {type Template} from '../types';
import {transformApiTemplateToTemplate} from '../transformers/template-transformers';
import type {ApiTemplateInput} from '../api-types';

type Result = {
    planTemplates: Array<Template>;
    loading: boolean;
    savePlanTemplate: (template: ApiTemplateInput) => Promise<FetchResult<SavePlanTemplateMutation>>;
};

export const usePlanTemplates = ({siteId}: {siteId: string}): Result => {
    const {canAccessUSPlans} = useCanAccessUSPlans(siteId);

    const [{planTemplates}, {loading}] = useTransformQuery<GetPlanTemplatesQuery, GetPlanTemplatesQueryVariables>()(
        GetPlanTemplates,
        {
            skip: !canAccessUSPlans,
            variables: {
                siteId,
            },
        },
        ({data, loading, error}) => {
            if (loading || !hasValue(data) || error) {
                return {
                    planTemplates: [],
                };
            }

            return {
                planTemplates: data.finance.planTemplates.v3.templates.map(template =>
                    transformApiTemplateToTemplate(template),
                ),
            };
        },
    );

    const [savePlanTemplateMutation] = useMutation<SavePlanTemplateMutation, SavePlanTemplateMutationVariables>(
        SavePlanTemplate,
        {
            update: updateApolloCache<GetPlanTemplatesQuery, GetPlanTemplatesQueryVariables>()({
                type: 'query',
                node: GetPlanTemplates,
                cacheVariables: {
                    siteId,
                },
                updater(cache, result) {
                    return produce(cache, cacheDraft => {
                        cacheDraft.finance.planTemplates.v3.templates = [
                            ...cacheDraft.finance.planTemplates.v3.templates,
                            result.finance.planTemplates.v3.save,
                        ];
                    });
                },
            }),
        },
    );

    const savePlanTemplate = useCallback(
        async (template: ApiTemplateInput) => {
            return savePlanTemplateMutation({
                variables: {
                    siteId,
                    name: template.name,
                    templateId: template.templateId,
                    pricingGroupId: template.pricingGroupId,
                    billingFrequency: template.billingFrequency,
                    tuitionIds: template.tuitionIds,
                    feeIds: template.feeIds,
                    discountIds: template.discountIds,
                },
            });
        },
        [savePlanTemplateMutation, siteId],
    );

    return {
        planTemplates,
        loading,
        savePlanTemplate,
    };
};
